import { faCheckCircle, faPencil } from '@fortawesome/pro-light-svg-icons'
import { AdjustmentDeliveryNoteModelType } from '@teinor/erp/types/company/order/adjustmentDeliveryNote'
import { DisplayPriceGettingParams } from '../../../../../baseComponents/Displays/DisplayPrice'
import { DisplayStatus } from '../../../../../baseComponents/Displays/DisplayStatus'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, CustomRenderDate, EditRow } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { OnRowClick } from '../../../../../baseComponents/TwinTable/VirtualTableListing/types'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import locations from '../../../../../utils/globals/locations'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'

type TableAdjustmentDeliveryNotesProps = TableComponentProps & {
    onRowClick?: OnRowClick
}

const TableAdjustmentDeliveryNotes: React.FC<TableAdjustmentDeliveryNotesProps> = ({ setTableInstance, onRowClick }) => {
    const { t } = useTwinTranslation()
    const adjustmentDeliveryNoteStatusComplex: TwinDictionary = {
        0: { name: t('draft', 'Borrador'), id: 0 },
        1: { name: t('processed', 'Procesado'), id: 1 },
    }
    const columns: ColumnTableSchema<AdjustmentDeliveryNoteModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('nAssigned', 'Nº Asignado'),

            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('nAssigned', 'Nº Asignado') }
            },
            minWidth: 160,
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <RenderAdjustmentStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: adjustmentDeliveryNoteStatusComplex, label: t('statusDeliveryNote', 'Estado del albarán') }
            },
        },
        {
            id: 'BillingWorkingCenterId',
            dataKey: 'BillingWorkingCenterId',
            searchKey: 'BillingWorkingCenter.name',
            label: t('requestedBy', 'Pedido por'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.BillingWorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 120
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customName', 'Nombre personalizado'), }
            },
            hideColumnByDefault: true
        },
        {
            id: 'total_without_vat',
            dataKey: 'total_without_vat',
            label: t('totalWithoutVatS', 'B. imponible'),
            simpleLabel: t('totalWithoutVat', 'Base imponible'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('employee', 'Empleado'),
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('employee', 'Empleado') }
            },
        },
        {
            id: 'RepresentativeEmployeeId',
            dataKey: 'RepresentativeEmployeeId',
            label: t('representative', 'Representante'),
            searchKey: 'RepresentativeEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.RepresentativeEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('representative', 'Representante') }
            },
            minWidth: 130
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.adjustmentDeliveryNoteSingle, ['$id']]} name='adjustmentDeliveryNotes' columns={columns} getDataFrom='/api/app/order/adjustmentDeliveryNote/getAllAdjustmentDeliveryNotesComplete' onRowClick={onRowClick} setTableInstance={setTableInstance} />
    )
}

interface RenderAdjustmentStatusProps {
    value: string
}

export const RenderAdjustmentStatus: React.FC<RenderAdjustmentStatusProps> = ({ value }) => {
    const parsedValue = parseInt(value)
    const { t } = useTwinTranslation()
    if (parsedValue === 0) {
        return <DisplayStatus icon={faPencil} colorText='text-gray-97' text={t('draft', 'Borrador')} />
    }
    return <DisplayStatus icon={faCheckCircle} colorText='text-green-43' text={t('processed', 'Procesado')} />

}

export default TableAdjustmentDeliveryNotes